// src/components/Navbar.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Bell, 
  Menu, 
  User, 
  LogOut, 
  Sun, 
  Moon, 
  Monitor,
  Search,
  PackageMinus
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config'; // ← votre fichier de config
import clsx from 'clsx';

export default function Navbar({ setSidebarOpen }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('accessToken');

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // État pour le mode sombre
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Gestion du thème (light / dark / system)
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  // Vérifier le mode sombre au chargement et quand il change
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    // Vérifier initial
    checkDarkMode();

    // Observer les changements de classe sur l'élément html
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Appliquer le thème
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Récupération des données utilisateur + notifications
  useEffect(() => {
    if (!token) {
      navigate('/login-register');
      return;
    }

    const fetchData = async () => {
      try {
        const [profileRes, notifRes] = await Promise.all([
          axios.get(`${config.REACT_APP_API_URL}/user-profile`, {
            headers: { Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY },
          }),
          axios.get(`${config.REACT_APP_API_URL}/getnotificationcustomer`, {
            headers: { Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY },
          }),
        ]);

        setUser(profileRes.data);

        // Trier + limiter les notifications
        const sorted = (notifRes?.data?.result || [])
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 10);
        setNotifications(sorted);
      } catch (err) {
        console.error('Erreur chargement navbar:', err);
        localStorage.removeItem('accessToken');
        navigate('/login-register');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Rafraîchissement périodique des notifications (toutes les 60s)
    const interval = setInterval(() => fetchData(), 60000);
    return () => clearInterval(interval);
  }, [token, navigate]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${config.REACT_APP_API_URL}/updatenotificationcustomer/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY }
      });
      setNotifications(prev => prev.filter(n => n._id !== id));
    } catch (err) {
      console.error('Erreur marquage notification:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    setUser(null);
    setShowUserMenu(false);
    toast.success('Déconnexion réussie', { theme: theme === 'dark' ? 'dark' : 'light' });
    navigate('/login-register');
  };

  if (loading) {
    return (
      <div className="h-16 bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400 text-sm">Chargement...</div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer 
        position="top-right" 
        autoClose={4000} 
        theme={theme === 'dark' ? 'dark' : 'light'} 
        limit={3}
      />

      {/* Navbar sticky */}
      <header className="sticky top-0 z-40 w-full border-b bg-white/80 dark:bg-gray-950/80 backdrop-blur-md border-gray-200 dark:border-gray-800">
        <div className="max-w-screen-2xl mx-auto px-4 min-sm:px-6 min-lg:px-8">
          <div className="flex h-16 items-center justify-between">

            {/* Gauche : Burger mobile */}
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="min-md:hidden p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition"
                onClick={() => setSidebarOpen(true)}
                aria-label="Ouvrir le menu latéral"
              >
                <Menu className="h-6 w-6" />
              </button>

              {/* Optionnel : Logo ou titre */}
              {/* <div className="hidden min-md:flex items-center gap-2">
                <Building2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
                <span className="font-semibold text-lg text-gray-900">Speed Tunisia</span>
              </div> */}
            </div>

            {/* Droite : Actions */}
            <div className="flex items-center gap-2 min-sm:gap-4">

              {/* Bouton recherche (optionnel – à décommenter si besoin) */}
              {/* <button
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                aria-label="Rechercher"
              >
                <Search className="h-5 w-5" />
              </button> */}

              {/* Toggle thème */}
              <div className="relative group">
  <button
    className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
    onClick={() => {
      setTheme(prev => {
        if (prev === 'light') return 'dark';
        if (prev === 'dark') return 'system';
        return 'light';
      });
    }}
    aria-label="Changer le thème"
  >
    {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" /> }
  </button>

  {/* Petit tooltip (optionnel mais très apprécié) */}
  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
    {theme === 'light' ? 'Passer en sombre' :
     theme === 'dark' ? 'Suivre le système' :
     'Passer en clair'}
  </div>
</div>

              {/* Notifications */}
              <div className="relative">
                <button
                  className="p-2 rounded-lg text-gray-600  dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 relative focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  onClick={() => setShowNotifications(!showNotifications)}
                  aria-label={`Notifications${notifications.length ? ` (${notifications.length})` : ''}`}
                >
                  <Bell className="h-5 w-5" />
                  {notifications.length > 0 && (
                    <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-950 animate-pulse" />
                  )}
                </button>

                {showNotifications && (
                  <div 
                  className="absolute right-0 mt-3 w-80 min-sm:w-96 rounded-xl shadow-2xl border overflow-hidden z-50"
                    style={{
                      backgroundColor: isDarkMode ? '#111827' : '#ffffff',
                      borderColor: isDarkMode ? '#374151' : '#e5e7eb'
                    }}>
                    <div 
                      className="p-4 border-b"
                      style={{
                        borderBottomColor: isDarkMode ? '#1f2937' : '#e5e7eb'
                      }}>
                      <h3 
                        className="font-semibold text-base mb-0"
                        style={{
                          color: isDarkMode ? '#ffffff' : '#111827'
                        }}>
                        Nouvelles commandes
                      </h3>
                    </div>

                    <div className="max-h-[380px] overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.map((notif) => (
                          <div
                            key={notif._id}
                            className="px-4 py-3 cursor-pointer transition-colors border-b last:border-0"
                            style={{
                              borderBottomColor: isDarkMode ? '#1f2937' : '#f3f4f6',
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            onClick={() => {
                              markAsRead(notif._id);
                              navigate('/orders');
                              setShowNotifications(false);
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5">
                                <PackageMinus className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate"
                        style={{
                          color: isDarkMode ? '#ffffff' : '#111827'
                        }}>
                                  {notif.typeNotification} • Nouvelle commande
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-10 text-center text-gray-500 dark:text-gray-500">
                          Aucune nouvelle notification
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Menu utilisateur */}
              <div className="relative">
                <button
                  className="flex items-center gap-2 p-1.5 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-label="Menu utilisateur"
                >
                  <User className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-3 w-64 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">

                    <div className="p-2">
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition"
                      >
                        <LogOut className="h-4 w-4" />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}